import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  applyActionCode, 
  checkActionCode, 
  confirmPasswordReset,
  isSignInWithEmailLink
} from 'firebase/auth';
import { auth } from '../firebase';
import { performSignInWithEmailLink } from '../hooks/useMultiAuth';
import { 
  Box, Typography, CircularProgress, Alert, Button, Paper, TextField, 
  InputAdornment, IconButton, Container 
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export interface AuthActionHandlerProps {
  onSignInWithEmailLink?: (email: string, link: string) => Promise<any>;
}

export default function AuthActionHandler({ onSignInWithEmailLink }: AuthActionHandlerProps = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'input_password' | 'input_email'>('loading');
  const [message, setMessage] = useState('');
  const [targetEmail, setTargetEmail] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const executeSignIn = useCallback(async (emailToUse: string) => {
    if (onSignInWithEmailLink) {
      await onSignInWithEmailLink(emailToUse, window.location.href);
    } else {
      await performSignInWithEmailLink(emailToUse, window.location.href);
    }
  }, [onSignInWithEmailLink]);

  useEffect(() => {
    document.title = "Jets '14 | Account Verification";
  }, []);

  useEffect(() => {
    const isEmailLink = isSignInWithEmailLink(auth, window.location.href) || mode === 'signIn';

    if (!isEmailLink && (!mode || !oobCode)) {
      setStatus('error');
      setMessage('Invalid action link. Missing required parameters.');
      return;
    }

    const handleAction = async () => {
      try {
        if (isEmailLink) {
          let storedEmail: string | null = null;
          try {
            storedEmail = window.localStorage.getItem('emailForSignIn');
          } catch (e) {
            console.warn('Could not read from localStorage:', e);
          }

          if (storedEmail) {
            await executeSignIn(storedEmail.trim());
            try {
              window.localStorage.removeItem('emailForSignIn');
            } catch (e) {}
            setStatus('success');
            setMessage('Successfully signed in! You will be redirected shortly.');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 2000);
          } else {
            // Need user to provide the email
            setStatus('input_email');
          }
        } else if (mode === 'verifyEmail') {
          // Verify the email address
          await applyActionCode(auth, oobCode!);
          
          if (auth.currentUser) {
            await auth.currentUser.reload();
          }
          
          setStatus('success');
          setMessage('Your email has been successfully verified! You will be redirected shortly.');
          
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
          
        } else if (mode === 'resetPassword') {
          // Check if the action code is valid
          const info = await checkActionCode(auth, oobCode!);
          if (info.data?.email) {
            setTargetEmail(info.data.email);
          }
          setStatus('input_password');
        } else if (mode === 'recoverEmail') {
          await applyActionCode(auth, oobCode!);
          setStatus('success');
          setMessage('Email recovery processed successfully.');
        } else {
          setStatus('error');
          setMessage(`Unsupported action mode: ${mode}`);
        }
      } catch (error: any) {
        console.error("Auth action error:", error);
        setStatus('error');
        if (error?.code === 'auth/invalid-action-code' || error?.code === 'auth/expired-action-code') {
          setMessage('The action link is invalid or has expired. Please request a new link.');
        } else {
          setMessage(error?.message || 'Failed to process authentication request.');
        }
      }
    };

    handleAction();
  }, [mode, oobCode, navigate, executeSignIn]);

  const handleEmailLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmEmail || !confirmEmail.trim()) {
      setMessage('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');
    try {
      await executeSignIn(confirmEmail.trim());
      try {
        window.localStorage.removeItem('emailForSignIn');
      } catch (e) {}
      setStatus('success');
      setMessage('Successfully signed in! You will be redirected shortly.');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (error: any) {
      console.error("Email link sign-in error:", error);
      if (error?.code === 'auth/invalid-action-code' || error?.code === 'auth/expired-action-code') {
        setStatus('error');
        setMessage('The sign-in link is invalid or has expired. Please request a new link.');
      } else if (error?.code === 'auth/invalid-email') {
        setMessage('The email address entered does not match the sign-in link. Please check and try again.');
      } else {
        setMessage(error?.message || 'Failed to sign in with email link. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode as string, newPassword);
      setStatus('success');
      setMessage('Your password has been successfully reset! You will be redirected shortly.');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
    } catch (error: any) {
      setMessage(error?.message || 'Failed to reset password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 2 }}>
      <Container maxWidth="xs">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <img 
            src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
            alt="Chicago Jets Logo" 
            style={{ height: 80, marginBottom: 16 }} 
          />
          
          {status === 'loading' && (
            <Box sx={{ py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1">Processing your request...</Typography>
            </Box>
          )}
          
          {status === 'success' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>{message}</Alert>
              <Typography variant="body2" color="text.secondary">
                Redirecting you to Chicago Jets...
              </Typography>
              <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 3 }} disableElevation fullWidth>
                Go to Team Portal Now
              </Button>
            </Box>
          )}
          
          {status === 'error' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{message}</Alert>
              <Button variant="outlined" onClick={() => navigate('/')} fullWidth>
                Return to Login
              </Button>
            </Box>
          )}

          {status === 'input_email' && (
            <Box component="form" onSubmit={handleEmailLinkSubmit} sx={{ textAlign: 'left' }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, textAlign: 'center' }}>
                Confirm Your Email
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                Please confirm the email address where you received the sign-in link to complete logging in.
              </Typography>
              
              {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                margin="normal"
                size="small"
                required
                autoFocus
              />

              <Button 
                type="submit" 
                variant="contained" 
                fullWidth 
                disabled={isSubmitting}
                disableElevation 
                sx={{ mt: 2.5, mb: 1, py: 1 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Complete Sign In'}
              </Button>

              <Button 
                variant="text" 
                fullWidth 
                onClick={() => navigate('/')}
                color="inherit"
                size="small"
              >
                Cancel and return to login
              </Button>
            </Box>
          )}

          {status === 'input_password' && (
            <Box component="form" onSubmit={handlePasswordResetSubmit} sx={{ textAlign: 'left' }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, textAlign: 'center' }}>
                Reset Your Password
              </Typography>
              {targetEmail && (
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                  For account: <strong>{targetEmail}</strong>
                </Typography>
              )}
              
              {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                size="small"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                size="small"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button 
                type="submit" 
                variant="contained" 
                fullWidth 
                disabled={isSubmitting}
                disableElevation 
                sx={{ mt: 3, mb: 1, py: 1 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Save New Password'}
              </Button>

              <Button 
                variant="text" 
                fullWidth 
                onClick={() => navigate('/')}
                color="inherit"
                size="small"
              >
                Cancel and return to login
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
